import { SerializedEditorState } from 'lexical'
import _ from 'lodash'
import { useMemo } from 'react'

import { useGroupFilter } from '../../../groupFilter'
import useLocationMetricValuesQuery from '../../../hooks/useLocationMetricValuesQuery'
import calc from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiType } from '../../types'
import { IDataType } from '../useData'

type CalcType = ReturnType<typeof calc>
interface IRowType extends Pick<IDataType, 'drop'> {
  [key: string]: unknown
  id: string
  parentId: string
  displayOrder?: number
  pAndLGlCode?: string
  pAndLName: string
  pAndLSales: CalcType
  pAndLSalesPercent: CalcType
  pAndLYoySales: CalcType
  pAndLYoySalesPercent: CalcType
  pAndLYoyCompare: CalcType
  pAndLYoyComparePercent: CalcType
  pAndLYtdSales: CalcType
  pAndLYtdSalesPercent: CalcType
  pAndLLYYtdSales: CalcType
  pAndLLYYtdSalesPercent: CalcType
  pAndLLYYtdCompare: CalcType
  pAndLLYYtdComparePercent: CalcType
  pAndLPriorPeriodSales: CalcType
  pAndLPriorPeriodSalesPercent: CalcType
  pAndLPriorPeriodCompare: CalcType
  pAndLPriorPeriodComparePercent: CalcType
  formula?: string
  isFormulaGroup?: boolean
}

export const pAndLConfigs = {
  pAndLName: 'string',
  pAndLGlCode: 'string',
  pAndLCategory: 'string',
  pAndLType: 'string',
  pAndLSales: 'price',
  pAndLSalesPercent: 'percent',
  pAndLBudget: 'price',
  pAndLBudgetPercent: 'percent',
  pAndLCompare: 'price',
  pAndLComparePercent: 'percent',
  pAndLYoySales: 'price',
  pAndLYoySalesPercent: 'percent',
  pAndLYoyCompare: 'price',
  pAndLYoyComparePercent: 'percent',
  pAndLYtdSales: 'price',
  pAndLYtdSalesPercent: 'percent',
  pAndLYtdBudget: 'price',
  pAndLYtdBudgetPercent: 'percent',
  pAndLYtdCompare: 'price',
  pAndLYtdComparePercent: 'percent',
  pAndLLYYtdSales: 'price',
  pAndLLYYtdSalesPercent: 'percent',
  pAndLLYYtdCompare: 'price',
  pAndLLYYtdComparePercent: 'percent',
  pAndLPriorPeriodSales: 'price',
  pAndLPriorPeriodSalesPercent: 'percent',
  pAndLPriorPeriodCompare: 'price',
  pAndLPriorPeriodComparePercent: 'percent',
  edit: 'button',
  add: 'button',
} as const

const usePAndL = (): IApiType => {
  const { variables } = useVariables()
  const glData = variables.manageGls.data
  const glCodes = glData?.listGlCodes?.nodes ?? []
  const glGroups = glData?.listGlGroups?.nodes ?? []
  const draggable = Boolean(variables.pnlDrop?.onDrop)
  const { groupFilter } = useGroupFilter()
  const isLegalEntity = groupFilter?.id.indexOf('group') === 0

  const { data, yoyData, ytdData, priorYtdData, priorData, loading } =
    useLocationMetricValuesQuery({
      metricGroups: [
        'PnL',
        { key: 'PnL', type: 'yoy' },
        { key: 'PnL', type: 'ytd' },
        { key: 'PnL', type: 'prior ytd' },
        { key: 'PnL', type: 'prior' },
      ],
      fields: ['metricSummaryData'],
      handler: (variables) => {
        const newVariables = {
          ...variables,
          iFilter: {
            ...variables.iFilter,
            ...(isLegalEntity
              ? {
                  location_group_ids: groupFilter?.ids,
                }
              : {
                  location_ids: groupFilter?.ids,
                }),
          },
        }

        return {
          variables: newVariables,
          skip:
            !groupFilter ||
            !newVariables.iStartDate ||
            !newVariables.iEndDate ||
            (newVariables.iFilter.metric_groups || []).length === 0,
        }
      },
    })

  const metricData =
    data?.filter(({ metricSummaryData }) => metricSummaryData)?.[0]
      ?.metricSummaryData ?? {}
  const yoyMetricData =
    yoyData?.filter(({ metricSummaryData }) => metricSummaryData)?.[0]
      ?.metricSummaryData ?? {}
  const ytdMetricData =
    ytdData?.filter(({ metricSummaryData }) => metricSummaryData)?.[0]
      ?.metricSummaryData ?? {}
  const lyYtdMetricData =
    priorYtdData?.filter(({ metricSummaryData }) => metricSummaryData)?.[0]
      ?.metricSummaryData ?? {}
  const priorPeriodMetricData =
    priorData?.filter(({ metricSummaryData }) => metricSummaryData)?.[0]
      ?.metricSummaryData ?? {}

  return {
    data: useMemo(() => {
      if (!metricData) return null

      const getSalesForGlGroup = (glGroupId: string) => {
        const glNamesInGroup = glCodes
          .filter(({ parentGroupId }) => parentGroupId === glGroupId)
          .map(({ name }) => name)
        const pAndLSales = Object.values(metricData)
          .filter(({ name }) => glNamesInGroup.includes(name))
          .reduce((acc, cur) => acc + cur.value, 0)
        const pAndLYoySales = Object.values(yoyMetricData)
          .filter(({ name }) => glNamesInGroup.includes(name))
          .reduce((acc, cur) => acc + cur.value, 0)
        const pAndLYtdSales = Object.values(ytdMetricData ?? {})
          .filter(({ name }) => glNamesInGroup.includes(name))
          .reduce((acc, cur) => acc + cur.value, 0)
        const pAndLLYYtdSales = Object.values(lyYtdMetricData ?? {})
          .filter(({ name }) => glNamesInGroup.includes(name))
          .reduce((acc, cur) => acc + cur.value, 0)
        const pAndLPriorPeriodSales = Object.values(priorPeriodMetricData ?? {})
          .filter(({ name }) => glNamesInGroup.includes(name))
          .reduce((acc, cur) => acc + cur.value, 0)

        const glGroupsInGroup = glGroups
          .filter(({ parentGroupId }) => parentGroupId === glGroupId)
          .map(({ id }) => id)

        if (glGroupsInGroup.length === 0) {
          return {
            pAndLSales,
            pAndLYoySales,
            pAndLYtdSales,
            pAndLLYYtdSales,
            pAndLPriorPeriodSales,
          }
        }

        let totalSubSales: CalcType = 0
        let totalSubYoySales: CalcType = 0
        let totalSubYtdSales: CalcType = 0
        let totalSubLYYtdSales: CalcType = 0
        let totalSubPriorPeriodSales: CalcType = 0

        for (const glSubgroupId of glGroupsInGroup) {
          const {
            pAndLSales: subSales,
            pAndLYoySales: subYoySales,
            pAndLYtdSales: subYtdSales,
            pAndLLYYtdSales: subLYYtdSales,
            pAndLPriorPeriodSales: subPriorPeriodSales,
          } = getSalesForGlGroup(glSubgroupId)

          totalSubSales = calc(totalSubSales, '+', subSales)
          totalSubYoySales = calc(totalSubYoySales, '+', subYoySales)
          totalSubYtdSales = calc(totalSubYtdSales, '+', subYtdSales)
          totalSubLYYtdSales = calc(totalSubLYYtdSales, '+', subLYYtdSales)
          totalSubPriorPeriodSales = calc(
            totalSubPriorPeriodSales,
            '+',
            subPriorPeriodSales,
          )
        }

        return {
          pAndLSales: calc(pAndLSales, '+', totalSubSales),
          pAndLYoySales: calc(pAndLYoySales, '+', totalSubYoySales),
          pAndLYtdSales: calc(pAndLYtdSales, '+', totalSubYtdSales),
          pAndLLYYtdSales: calc(pAndLLYYtdSales, '+', totalSubLYYtdSales),
          pAndLPriorPeriodSales: calc(
            pAndLPriorPeriodSales,
            '+',
            totalSubPriorPeriodSales,
          ),
        }
      }

      const groupRows: IRowType[] = []
      const salesGlGroup =
        glGroups.filter(({ exrayCategory }) => exrayCategory === 'Sales')[0] ??
        {}
      const {
        pAndLSales: totalSales,
        pAndLYoySales: yoyTotalSales,
        pAndLYtdSales: ytdTotalSales,
        pAndLLYYtdSales: lyYtdTotalSales,
        pAndLPriorPeriodSales: priorPeriodTotalSales,
      } = getSalesForGlGroup(salesGlGroup.id)

      const calcDetail = ({
        pAndLSales,
        pAndLYoySales,
        pAndLYtdSales,
        pAndLLYYtdSales,
        pAndLPriorPeriodSales,
      }: ReturnType<typeof getSalesForGlGroup>) => {
        const pAndLYoyCompare = calc(pAndLSales, '-', pAndLYoySales)
        const pAndLLYYtdCompare = calc(pAndLYtdSales, '-', pAndLLYYtdSales)
        const pAndLPriorPeriodCompare = calc(
          pAndLSales,
          '-',
          pAndLPriorPeriodSales,
        )

        return {
          pAndLSales,
          pAndLSalesPercent: calc(pAndLSales, 'percentageOf', totalSales),
          pAndLYoySales,
          pAndLYoySalesPercent: calc(
            pAndLYoySales,
            'percentageOf',
            yoyTotalSales,
          ),
          pAndLYoyCompare,
          pAndLYoyComparePercent: calc(
            pAndLYoyCompare,
            'percentageOf',
            Math.abs(pAndLYoySales as number),
          ),
          pAndLYtdSales,
          pAndLYtdSalesPercent: calc(
            pAndLYtdSales,
            'percentageOf',
            ytdTotalSales,
          ),
          pAndLLYYtdSales,
          pAndLLYYtdSalesPercent: calc(
            pAndLLYYtdSales,
            'percentageOf',
            lyYtdTotalSales,
          ),
          pAndLLYYtdCompare,
          pAndLLYYtdComparePercent: calc(
            pAndLLYYtdCompare,
            'percentageOf',
            Math.abs(pAndLLYYtdSales as number),
          ),
          pAndLPriorPeriodSales,
          pAndLPriorPeriodSalesPercent: calc(
            pAndLPriorPeriodSales,
            'percentageOf',
            priorPeriodTotalSales,
          ),
          pAndLPriorPeriodCompare,
          pAndLPriorPeriodComparePercent: calc(
            pAndLPriorPeriodCompare,
            'percentageOf',
            Math.abs(pAndLPriorPeriodSales as number),
          ),
        }
      }

      for (const glGroup of glGroups.filter((g) => !g.formula)) {
        const data = getSalesForGlGroup(glGroup.id)

        groupRows.push({
          ...data,
          ...calcDetail(data),
          id: `folder-${glGroup.id}`,
          parentId:
            glGroup.parentGroupId === null
              ? 'root'
              : `folder-${glGroup.parentGroupId}`,
          pAndLName: glGroup.displayName,
          edit: 'EDIT',
          add: 'ADD',
          displayOrder: glGroup.displayOrder,
          drop: {
            isFrozen: glGroup.exrayCategory === 'Net Profit',
            minDepth: 1,
          },
        } as IRowType)
      }

      const itemRows = []

      for (const glCode of glCodes) {
        const pAndLSales =
          Object.values(metricData).find(({ name }) => glCode.name === name)
            ?.value ?? null
        const pAndLYoySales =
          Object.values(yoyMetricData).find(({ name }) => glCode.name === name)
            ?.value ?? null
        const pAndLYtdSales =
          Object.values(ytdMetricData ?? {}).find(
            ({ name }) => glCode.name === name,
          )?.value ?? null
        const pAndLLYYtdSales =
          Object.values(lyYtdMetricData ?? {}).find(
            ({ name }) => glCode.name === name,
          )?.value ?? null
        const pAndLPriorPeriodSales =
          Object.values(priorPeriodMetricData ?? {}).find(
            ({ name }) => glCode.name === name,
          )?.value ?? null
        const data = {
          pAndLSales,
          pAndLYoySales,
          pAndLYtdSales,
          pAndLLYYtdSales,
          pAndLPriorPeriodSales,
        } as ReturnType<typeof getSalesForGlGroup>

        const item = {
          ...data,
          ...calcDetail(data),
          id: `item-${glCode.id}`,
          parentId: `folder-${glCode.parentGroupId}`,
          pAndLName: `${glCode.code} - ${glCode.name}`,
          pAndLGlCode: glCode.code,
          displayOrder: glCode.displayOrder,
          drop: {
            disableSubRows: true,
            minDepth: 1,
          },
        } as IRowType

        if (item.pAndLSales || draggable) itemRows.push(item)
      }

      const rows = [...itemRows, ...groupRows]

      for (const glGroup of glGroups.filter((g) => g.formula)) {
        const { formula } = glGroup
        const allFormula = (
          formula as unknown as SerializedEditorState<{
            type: string
            version: number
            children: Record<string, unknown>[]
          }>
        ).root.children
          .map((d) => d.children)
          .flat()

        const data = (
          allFormula as {
            type: 'select-node' | 'text'
            text: string
            moreDetails: { id: string }
          }[]
        ).reduce(
          (
            result: ReturnType<typeof getSalesForGlGroup>,
            f,
            index,
            allData,
          ) => {
            switch (f.type) {
              case 'select-node': {
                const row = rows.find((r) => r.id === f.moreDetails.id)
                const operator =
                  index === 0 || allData[index - 1]?.text.trim() !== '-'
                    ? '+'
                    : '-'

                return (
                  Object.keys(result) as (keyof ReturnType<
                    typeof getSalesForGlGroup
                  >)[]
                ).reduce(
                  (subResult, key) => ({
                    ...subResult,
                    [key]:
                      _.isNil(subResult[key]) && _.isNil(row?.[key])
                        ? null
                        : calc(subResult[key] || 0, operator, row?.[key] || 0),
                  }),
                  result,
                )
              }
              default:
                return result
            }
          },
          {
            pAndLSales: null,
            pAndLYoySales: null,
            pAndLYtdSales: null,
            pAndLLYYtdSales: null,
            pAndLPriorPeriodSales: null,
          } as ReturnType<typeof getSalesForGlGroup>,
        )

        rows.push({
          ...data,
          ...calcDetail(data),
          id: `folder-${glGroup.id}`,
          parentId: 'root',
          pAndLName: glGroup.displayName,
          displayOrder: glGroup.displayOrder,
          edit: 'EDIT',
          formula: JSON.stringify(glGroup.formula),
          isFormulaGroup: true,
          drop: {
            group: 'formula',
            disableSubRows: true,
          },
        } as IRowType)
      }

      return rows.sort((a, b) => {
        if (a.displayOrder && b.displayOrder) {
          return a.displayOrder - b.displayOrder
        }

        if (
          !a.displayOrder &&
          !b.displayOrder &&
          a.pAndLGlCode &&
          b.pAndLGlCode
        ) {
          return a.pAndLGlCode.localeCompare(b.pAndLGlCode)
        }

        if (!a.displayOrder) {
          return a.isFormulaGroup ? 1 : -1
        }

        if (!b.displayOrder) {
          return b.isFormulaGroup ? -1 : 1
        }

        return -1
      })
    }, [data, yoyData, ytdData, priorYtdData, variables.manageGls, draggable]),
    loading: loading || Boolean(variables.pnlDrop?.loading),
  }
}

export default usePAndL
